import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './TripAI.css';
import Navbar from '../layout/navbar';
import LocationsConfig from './LocationsConfig';
import FoodConfig from './FoodConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Global counter for Google Places API calls
let placesApiCallCounter = 0;

// Function to increment and log the API call counter
const incrementPlacesApiCounter = (source = 'direct') => {
  placesApiCallCounter++;
  console.log(`PLACE API CALL = ${placesApiCallCounter} (${source})`);
};

export default function TripAI() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get tripId from location.state
  const tripId = location?.state?.tripId;
  const tripName = location?.state?.tripName || 'Your Trip';
  
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showQuotaPopup, setShowQuotaPopup] = useState(false);
  
  // Single state to track what type of insights we're currently viewing
  const [insightType, setInsightType] = useState('Locations');
  
  const [addingPlaces, setAddingPlaces] = useState({});
  const [removingPlaces, setRemovingPlaces] = useState({});
  const [selectedPlaces, setSelectedPlaces] = useState({});
  const [searchLocation, setSearchLocation] = useState("");
  const [addedPlaces, setAddedPlaces] = useState(new Set());
  const [removedPlaces, setRemovedPlaces] = useState(new Set());
  const [openDropdowns, setOpenDropdowns] = useState({});
  const inputRef = useRef(null);
  const [insightKey, setInsightKey] = useState(0);
  
  const [hasSearched, setHasSearched] = useState(false); // Track if user has searched
  
  // Store results separately for each insight type
  const [locationInsights, setLocationInsights] = useState(null);
  const [foodInsights, setFoodInsights] = useState(null);
  const [hasSearchedLocations, setHasSearchedLocations] = useState(false);
  const [hasSearchedFood, setHasSearchedFood] = useState(false);
  
  const [relaxation, setRelaxation] = useState(0.5);
  const [adventure, setAdventure] = useState(0.5);
  const [cultural, setCultural] = useState(0.5);
  const [nature, setNature] = useState(0.5);
  const [luxury, setLuxury] = useState(0.5);
  const [romantic, setRomantic] = useState(0.5);
  const [family, setFamily] = useState(0.5);
  const [searchComment, setSearchComment] = useState('');
  const [pendingConfig, setPendingConfig] = useState({ relaxation, adventure, cultural, nature, luxury, romantic, family , searchComment });
  const configRef = useRef({ relaxation, adventure, cultural, nature, luxury, romantic, family , searchComment });
  
  const [enabledCities, setEnabledCities] = useState(() =>
    (location?.state?.cities || []).map(() => true)
  );
  const [showDaySelect, setShowDaySelect] = useState(false);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [trip, setTrip] = useState([]);
  const [selectedDayIdx, setSelectedDayIdx] = useState(null);
  const [inlineAddIdx, setInlineAddIdx] = useState(null);
  const [inlineSelectedDayIdx, setInlineSelectedDayIdx] = useState(null);

  // Update insights display when insight type changes
  useEffect(() => {
    if (insightType === 'Locations') {
      setInsights(locationInsights);
      setHasSearched(hasSearchedLocations);
    } else {
      setInsights(foodInsights);
      setHasSearched(hasSearchedFood);
    }
  }, [insightType, locationInsights, foodInsights, hasSearchedLocations, hasSearchedFood]);
  
  // Food-specific configuration states
  const [priceRange, setPriceRange] = useState('mid-range');
  const [cuisineTypes, setCuisineTypes] = useState(['Everything']);
  const [experience, setExperience] = useState('Casual');
  const [foodSearchComment, setFoodSearchComment] = useState('');
  const [pendingFoodConfig, setPendingFoodConfig] = useState({ 
    priceRange: 'mid-range', 
    cuisineTypes: ['Everything'], 
    experience: 'Casual',
    searchComment: ''
  });

  // Update configRef on change
  useEffect(() => {
    configRef.current = { relaxation, adventure, cultural, nature, luxury, romantic, family,searchComment };
  }, [relaxation, adventure, cultural, nature, luxury, romantic, family,searchComment]);

  // Keep pendingConfig in sync with sliders
  useEffect(() => {
    setPendingConfig({ relaxation, adventure, cultural, nature, luxury, romantic, family, searchComment });
  }, [relaxation, adventure, cultural, nature, luxury, romantic, family, searchComment]);

  // Sync individual states with pendingConfig in real time
  useEffect(() => {
    setRelaxation(pendingConfig.relaxation);
    setAdventure(pendingConfig.adventure);
    setCultural(pendingConfig.cultural);
    setNature(pendingConfig.nature);
    setLuxury(pendingConfig.luxury);
    setRomantic(pendingConfig.romantic);
    setFamily(pendingConfig.family);
    setSearchComment(pendingConfig.searchComment);
  }, [pendingConfig]);

  // Keep pendingFoodConfig in sync with food states
  useEffect(() => {
    setPendingFoodConfig({ priceRange, cuisineTypes, experience, searchComment: foodSearchComment });
  }, [priceRange, cuisineTypes, experience, foodSearchComment]);

  // Fetch insights with config and searchComment - only when manually triggered
  const fetchInsights = (configOverride = null) => {
    if (!tripId) {
      setError('No trip ID provided.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(''); // Clear any previous errors
    // Don't update hasSearched here - it's handled in handleSearch

    const enabledCityNames = (location?.state?.cities || []).filter((_, idx) => enabledCities[idx]);
    
    // Use override config if provided, otherwise use current state
    const locationConfig = configOverride?.locations || {
      relaxation,
      adventure,
      cultural,
      nature,
      luxury,
      romantic,
      family,
      searchComment
    };
    
    const foodConfig = configOverride?.food || {
      priceRange,
      cuisineTypes,
      experience,
      searchComment: foodSearchComment
    };
    
    // Prepare request body based on insight type
    const requestBody = {
      insightType: insightType,
      enabledCities: enabledCityNames,
      ...(insightType === 'Locations' ? locationConfig : foodConfig)
    };

    fetch(`${BACKEND_URL}/trip-ai-insights/${tripId}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    })
      .then(res => {
        if (!res.ok) {
          if (res.status === 429) {
            // Daily quota limit reached
            setShowQuotaPopup(true);
            throw new Error('Daily quota limit reached');
          }
          throw new Error('Failed to fetch AI insights');
        }
        return res.json();
      })
      .then(data => {
        // Store results based on insight type
        if (insightType === 'Locations') {
          setLocationInsights(data);
          setHasSearchedLocations(true);
        } else {
          setFoodInsights(data);
          setHasSearchedFood(true);
        }
        setInsights(data);
        console.log('AI Insights:', data);
        setLoading(false);
      })
      .catch(err => {
        // Don't show error message if it's a quota limit (popup will show instead)
        if (!err.message.includes('Daily quota limit reached')) {
          setError(err.message);
        }
        setLoading(false);
      }); 
  };

  // Only fetch insights when search button is pressed
  const handleSearch = () => {
    if (insightType === 'Locations') {
      setRelaxation(pendingConfig.relaxation);
      setAdventure(pendingConfig.adventure);
      setCultural(pendingConfig.cultural);
      setNature(pendingConfig.nature);
      setLuxury(pendingConfig.luxury);
      setRomantic(pendingConfig.romantic);
      setFamily(pendingConfig.family);
      setSearchComment(pendingConfig.searchComment);
    } else {
      setPriceRange(pendingFoodConfig.priceRange);
      setCuisineTypes(pendingFoodConfig.cuisineTypes);
      setExperience(pendingFoodConfig.experience);
      setFoodSearchComment(pendingFoodConfig.searchComment);
    }
    
    // Update hasSearched for the current insight type
    setHasSearched(true);
    
    // Pass the pending configs directly to fetchInsights to avoid timing issues
    const configOverride = {
      locations: {
        relaxation: pendingConfig.relaxation,
        adventure: pendingConfig.adventure,
        cultural: pendingConfig.cultural,
        nature: pendingConfig.nature,
        luxury: pendingConfig.luxury,
        romantic: pendingConfig.romantic,
        family: pendingConfig.family,
        searchComment: pendingConfig.searchComment
      },
      food: {
        priceRange: pendingFoodConfig.priceRange,
        cuisineTypes: pendingFoodConfig.cuisineTypes,
        experience: pendingFoodConfig.experience,
        searchComment: pendingFoodConfig.searchComment
      }
    };
    
    // Trigger the search with the current pending config values
    fetchInsights(configOverride);
  };

  function getStepLabel(val) {
    if (val === 0) return 'Ignore';
    if (val === 0.5) return 'Include';
    if (val === 1) return 'Focus';
    return '';
  }

  function renderConfigPane() {
    const isMobile = window.innerWidth <= 768;

    return (
      <div className="trip-ai-config-pane">
        <div
          className="trip-ai-config-title"
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: isMobile ? 4 : 0
          }}
        >
          <select
            value={insightType}
            onChange={e => setInsightType(e.target.value)}
            style={{
              marginRight: 2,
              padding: '4px 8px',
              fontSize: '1em',
              borderRadius: '4px',
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              minWidth: '130px',
              width: 'auto',
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e\")",
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 4px center',
              backgroundSize: '16px',
              paddingRight: '24px'
            }}
          >
            <option value="Locations">Locations</option>
            <option value="Food">Food</option>
          </select>
          <span style={{ fontWeight: 500 }}>Insights for </span>
          <button
            style={{
              background: "none",
              border: "none",
              color: "#2a5d9f",
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "1em",
              paddingLeft: 5,
              paddingRight: 2
            }}
            onClick={() => {
              navigate(`/trip-details/${tripId}`, { 
                state: { 
                  trip: {
                    _id: tripId,
                    name: tripName,
                    days: trip
                  }
                } 
              });
            }}
            disabled={!tripId}
          >
            {tripName}
          </button>
          <span style={{ fontWeight: 500 }}>Trip</span>
        </div>

        <div className="trip-ai-locations-row">
          <span className="trip-ai-locations-label">Locations:</span>
          <div className="trip-ai-locations-btns">
            {(location?.state?.cities || []).map((city, idx) => (
              <button
                key={idx}
                className={`trip-ai-location-btn ${!enabledCities[idx] ? 'disabled' : ''}`}
                onClick={() => {
                  setEnabledCities(prev => {
                    const newEnabled = [...prev];
                    newEnabled[idx] = !newEnabled[idx];
                    return newEnabled;
                  });
                }}
                style={{
                  opacity: enabledCities[idx] ? 1 : 0.6,
                }}
              >
                {city}
              </button>
            ))}
          </div>
        </div>

        {/* Render appropriate configuration based on insight type */}
        {insightType === 'Locations' ? (
          <LocationsConfig 
            pendingConfig={pendingConfig}
            setPendingConfig={setPendingConfig}
            isMobile={isMobile}
          />
        ) : (
          <FoodConfig 
            pendingFoodConfig={pendingFoodConfig}
            setPendingFoodConfig={setPendingFoodConfig}
            setFoodSearchComment={setFoodSearchComment}
            isMobile={isMobile}
          />
        )}

        {/* Search button - positioned consistently for both types */}
        <div className={`config-search-row config-search-row-top`} style={{ 
          position: 'relative', 
          width: '100%', 
          marginTop: 8,
          marginBottom: 8,
          display: 'flex',
          justifyContent: 'flex-end',
          ...(isMobile ? { marginTop: 0, marginBottom: 4 } : {})
        }}>
          <button
            className="config-search-btn config-search-btn-circle"
            type="button"
            onClick={handleSearch}
            aria-label="Search"
            style={{ width: 32, height: 32, fontSize: '1em' }}
          >
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="9" cy="9" r="7" stroke="white" strokeWidth="2"/>
              <line x1="14.2" y1="14.2" x2="18" y2="18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>
    );
  }

  // Fetch trip days on mount (assuming tripId is available)
  useEffect(() => {
    if (tripId) {
      fetch(`${BACKEND_URL}/newtrips/${tripId}`, {
        method: 'GET',
        credentials: 'include',
      })
        .then(res => res.json())
        .then(data => setTrip(data.days || []))
        .catch(err => console.error('Error fetching trip:', err));
    }
  }, [tripId]);

  // Helper to format AI insights for mobile
  function renderInsightsMobile(insights) {
    if (!insights || !insights.travelPlan || !Array.isArray(insights.travelPlan)) {
      return (
        <div className="ai-insights-fullwidth">
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            No insights available.
          </div>
        </div>
      );
    }

    const plans = insights.travelPlan;

    return (
      <div className="ai-insights ai-insights-fullwidth">
        <div className="ai-insights-list">
          {plans.map((loc, idx) => {
            const isAdded = addedPlaces.has(idx) || isInsightInTrip(loc);
            const isRemoved = removedPlaces.has(idx);
            const showInlineAdd = inlineAddIdx === idx;

            return (
              <div
                key={idx}
                className="ai-insights-location ai-insights-tile"
                data-testid={`ai-insight-tile-${idx}`}
                style={{ 
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '12px',
                  marginBottom: '12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: '#fff'
                }}
              >
                {/* Mobile: Photo at top, full width */}
                <div style={{ 
                  width: '100%', 
                  height: '150px', 
                  marginBottom: '12px',
                  borderRadius: '6px',
                  overflow: 'hidden'
                }}>
                  { (loc.photoBlobBase64) ? (
                    <img
                      src={`data:image/jpeg;base64,${loc.photoBlobBase64}`}
                      alt={loc.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  )  : (
                    <div style={{ width: '100%', height: '100%', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: '12px', color: '#888' }}>No Image</span>
                    </div>
                  )}
                </div>

                {/* Mobile: Content below photo */}
                <div style={{ flex: 1 }}>
                  {insightType === 'Food' ? (
                    /* Food-specific mobile layout */
                    <>
                      {/* Restaurant name as main title */}
                      <div style={{ marginBottom: '8px' }}>
                        {loc.webUrl && loc.webUrl !== 'none' ? (
                          <a
                            className="ai-insights-location-title ai-insights-location-title-link"
                            href={loc.webUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              fontSize: '1.1em',
                              fontWeight: '600',
                              lineHeight: '1.3',
                              display: 'block'
                            }}
                          >
                            {(loc.name || '').replace(/^\*+|\*+$/g, '').trim()}
                          </a>
                        ) : (
                          <div
                            className="ai-insights-location-title"
                            style={{ 
                              fontSize: '1.1em',
                              fontWeight: '600',
                              lineHeight: '1.3',
                              display: 'block'
                            }}
                          >
                            {(loc.name || '').replace(/^\*+|\*+$/g, '').trim()}
                          </div>
                        )}
                      </div>

                      {/* Price and Rating row */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px',
                        marginBottom: '8px',
                        flexWrap: 'wrap'
                      }}>
                        {loc.priceLevel && (
                          <span style={{ 
                            fontSize: '0.9em',
                            color: '#2e7d32',
                            fontWeight: '500'
                          }}>
                            {'$'.repeat(loc.priceLevel)}
                          </span>
                        )}
                        
                        {(loc.rating || loc.total_ratings) && (
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '4px',
                            fontSize: '0.85em',
                            color: '#666'
                          }}>
                            {loc.rating && (
                              <>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                  {[1, 2, 3, 4, 5].map(star => (
                                    <span
                                      key={star}
                                      style={{
                                        color: star <= Math.round(loc.rating) ? '#ffa500' : '#ddd',
                                        fontSize: '12px'
                                      }}
                                    >
                                      ★
                                    </span>
                                  ))}
                                </div>
                                <span style={{ fontWeight: '500', marginLeft: '2px' }}>
                                  {loc.rating.toFixed(1)}
                                </span>
                              </>
                            )}
                            {loc.total_ratings && (
                              <span style={{ color: '#888' }}>
                                ({loc.total_ratings.toLocaleString()})
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    /* Location-specific mobile layout */
                    <>
                      {/* Location name as main title */}
                      <div style={{ marginBottom: '8px' }}>
                        {loc.infoUrl ? (
                          <a
                            className="ai-insights-location-title ai-insights-location-title-link"
                            href={loc.infoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              fontSize: '1.1em',
                              fontWeight: '600',
                              lineHeight: '1.3',
                              display: 'block'
                            }}
                          >
                            {(loc.name || '').replace(/^\*+|\*+$/g, '').trim()}
                          </a>
                        ) : (
                          <div
                            className="ai-insights-location-title"
                            style={{ 
                              fontSize: '1.1em',
                              fontWeight: '600',
                              lineHeight: '1.3',
                              display: 'block'
                            }}
                          >
                            {(loc.name || '').replace(/^\*+|\*+$/g, '').trim()}
                          </div>
                        )}
                      </div>

                      {/* Attributes and Rating row */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px',
                        marginBottom: '8px',
                        flexWrap: 'wrap'
                      }}>
                        {loc.attributes && loc.attributes.length > 0 && (
                          <div style={{ 
                            fontSize: '0.8em',
                            color: '#666',
                            fontStyle: 'italic'
                          }}>
                            {loc.attributes.join(', ')}
                          </div>
                        )}
                        
                        {(loc.rating || loc.total_ratings) && (
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '4px',
                            fontSize: '0.85em',
                            color: '#666'
                          }}>
                            {loc.rating && (
                              <>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                  {[1, 2, 3, 4, 5].map(star => (
                                    <span
                                      key={star}
                                      style={{
                                        color: star <= Math.round(loc.rating) ? '#ffa500' : '#ddd',
                                        fontSize: '12px'
                                      }}
                                    >
                                      ★
                                    </span>
                                  ))}
                                </div>
                                <span style={{ fontWeight: '500', marginLeft: '2px' }}>
                                  {loc.rating.toFixed(1)}
                                </span>
                              </>
                            )}
                            {loc.total_ratings && (
                              <span style={{ color: '#888' }}>
                                ({loc.total_ratings.toLocaleString()})
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Description */}
                  <div style={{ 
                    fontSize: '0.9em', 
                    lineHeight: '1.4', 
                    marginBottom: '12px',
                    color: '#333'
                  }}>
                    {loc.description}
                  </div>

                  {/* Address, Hours, Phone - stacked vertically for mobile */}
                  {loc.address && (
                    <div style={{ 
                      fontSize: '0.8em', 
                      color: '#666', 
                      marginBottom: '4px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '6px'
                    }}>
                      <span>📍</span>
                      <span style={{ flex: 1, lineHeight: '1.3' }}>{loc.address}</span>
                    </div>
                  )}
                  
                  {loc.openingHours && (
                    <div style={{ 
                      fontSize: '0.8em', 
                      color: '#666', 
                      marginBottom: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span>🕐</span>
                      <span>{loc.openingHours}</span>
                    </div>
                  )}
                  
                  {loc.phoneNumber && (
                    <div style={{ 
                      fontSize: '0.8em', 
                      color: '#666', 
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span>📞</span>
                      <span>{loc.phoneNumber}</span>
                    </div>
                  )}

                  {/* Mobile: Action buttons at bottom */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 'auto',
                    paddingTop: '8px'
                  }}>
                    {/* Day label when added */}
                    {!showInlineAdd && isAdded && !isRemoved && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        background: '#e8f5e8',
                        border: '1px solid #4CAF50',
                        borderRadius: '6px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        color: '#2e7d32',
                        fontWeight: '500'
                      }}>
                        📍 {getInsightTripDay(loc)}
                      </div>
                    )}

                    {/* Inline add dropdown for mobile */}
                    {showInlineAdd && (
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                        flex: 1,
                        background: '#f8f9fa',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        padding: '8px'
                      }}>
                        <select
                          value={inlineSelectedDayIdx !== null ? inlineSelectedDayIdx : ''}
                          onChange={(e) => setInlineSelectedDayIdx(parseInt(e.target.value))}
                          style={{
                            border: '1px solid #ccc',
                            background: 'white',
                            fontSize: '12px',
                            padding: '6px 8px',
                            borderRadius: '4px',
                            outline: 'none',
                            flex: 1,
                            cursor: 'pointer'
                          }}
                        >
                          <option value="">Select Day</option>
                          {trip.map((day, dayIdx) => (
                            <option key={dayIdx} value={dayIdx}>
                              {day.description || `Day ${dayIdx + 1}`}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={async () => {
                            if (inlineSelectedDayIdx !== null) {
                              await handleAddToTrip(loc, inlineSelectedDayIdx);
                              setAddedPlaces(prev => new Set([...prev, idx]));
                              setRemovedPlaces(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(idx);
                                return newSet;
                              });
                              setInlineAddIdx(null);
                              setInlineSelectedDayIdx(null);
                            }
                          }}
                          style={{
                            background: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          ✓
                        </button>
                        <button
                          onClick={() => {
                            setInlineAddIdx(null);
                            setInlineSelectedDayIdx(null);
                          }}
                          style={{
                            background: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          ✗
                        </button>
                      </div>
                    )}

                    {/* Add/Remove buttons */}
                    {!showInlineAdd && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {(!isAdded || isRemoved) && (
                          <button
                            className="ai-insights-add-btn"
                            onClick={() => setInlineAddIdx(idx)}
                            title="Add to trip"
                            style={{
                              background: '#4CAF50',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 16px',
                              fontSize: '14px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            + Add
                          </button>
                        )}
                        
                        {isAdded && !isRemoved && (
                          <button
                            className="ai-insights-remove-btn"
                            onClick={async () => {
                              await handleRemoveFromTrip(loc);
                              setRemovedPlaces(prev => new Set([...prev, idx]));
                              setAddedPlaces(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(idx);
                                return newSet;
                              });
                            }}
                            title="Remove from trip"
                            style={{
                              background: '#f44336',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 16px',
                              fontSize: '14px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Helper to format AI insights
  function renderInsights(insights) {
    if (!insights || !insights.travelPlan || !Array.isArray(insights.travelPlan)) {
      return (
        <div className="ai-insights-fullwidth">
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            No insights available.
          </div>
        </div>
      );
    }

    const plans = insights.travelPlan;

    return (
      <div className="ai-insights ai-insights-fullwidth">
        
        <div className="ai-insights-list">
          {plans.map((loc, idx) => {
            const isAdded = addedPlaces.has(idx) || isInsightInTrip(loc);
            const isRemoved = removedPlaces.has(idx);
            const showInlineAdd = inlineAddIdx === idx;
            

            return (
              <div
                key={idx}
                className="ai-insights-location ai-insights-tile ai-insights-flex-tile"
                data-testid={`ai-insight-tile-${idx}`}
                style={{ 
                  position: 'relative',
                  height: 'auto',
                  minHeight: '210px'
                  
                }}
              >
                {/* Left: Photo (20%) */}
                <div className="ai-insights-collage-pane">
                  { (loc.photoBlobBase64) ? (
                    <img
                      src={`data:image/jpeg;base64,${loc.photoBlobBase64}`}
                      alt={loc.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  )  : (
                    <div style={{ width: '100%', height: '100%', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: '12px', color: '#888' }}>No Image</span>
                    </div>
                  )}
                </div>
                
                {/* Right: Info (80%) */}
                <div className="ai-insights-info-pane" style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {insightType === 'Food' ? (
                    /* Food-specific layout with proper row structure */
                    <>
                      {/* First row: Restaurant name, price, and rating */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        flexWrap: 'nowrap',
                        marginBottom: '6px',
                        width: '100%',
                        overflow: 'hidden'
                      }}>
                        {loc.webUrl && loc.webUrl !== 'none' ? (
                          <a
                            className="ai-insights-location-title ai-insights-location-title-link"
                            href={loc.webUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              flexShrink: 0,
                              display: 'inline-block',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              maxWidth: '40%'
                            }}
                          >
                            {(loc.name || '').replace(/^\*+|\*+$/g, '').trim()}
                          </a>
                        ) : (
                          <div
                            className="ai-insights-location-title"
                            style={{ 
                              flexShrink: 0,
                              display: 'inline-block',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              maxWidth: '40%'
                            }}
                          >
                            {(loc.name || '').replace(/^\*+|\*+$/g, '').trim()}
                          </div>
                        )}
                        
                        {/* Price Range for Food - Regular font weight */}
                        {loc.priceLevel && (
                          <span style={{ 
                            fontSize: '0.9em',
                            color: '#2e7d32',
                            fontWeight: '400',
                            flexShrink: 0,
                            display: 'inline-block',
                            whiteSpace: 'nowrap'
                          }}>
                            {'$'.repeat(loc.priceLevel)}
                          </span>
                        )}
                        
                        {/* Rating Stars and Count */}
                        {(loc.rating || loc.total_ratings) && (
                          <div style={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: '4px',
                            fontSize: '0.9em',
                            color: '#666',
                            flexShrink: 0,
                            whiteSpace: 'nowrap'
                          }}>
                            {loc.rating && (
                              <>
                                <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                                  {[1, 2, 3, 4, 5].map(star => (
                                    <span
                                      key={star}
                                      style={{
                                        color: star <= Math.round(loc.rating) ? '#ffa500' : '#ddd',
                                        fontSize: '14px',
                                        display: 'inline-block'
                                      }}
                                    >
                                      ★
                                    </span>
                                  ))}
                                </div>
                                <span style={{ fontWeight: '500', marginLeft: '2px', display: 'inline-block' }}>
                                  {loc.rating.toFixed(1)}
                                </span>
                              </>
                            )}
                            {loc.total_ratings && (
                              <span style={{ color: '#888', display: 'inline-block' }}>
                                ({loc.total_ratings.toLocaleString()})
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                     
                    </>
                  ) : (
                    /* Location-specific layout (original) */
                    <div className="ai-insights-location-title-row">
                      {/* Location Name, Rating, and Attributes - All on same line */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        flexWrap: 'nowrap',
                        marginBottom: '4px',
                        width: '100%',
                        overflow: 'hidden'
                      }}>
                        {loc.infoUrl ? (
                          <a
                            className="ai-insights-location-title ai-insights-location-title-link"
                            href={loc.infoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              flexShrink: 0,
                              display: 'inline-block',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              maxWidth: window.innerWidth <= 768 ? '100%' : '40%'
                            }}
                          >
                            {(loc.name || '').replace(/^\*+|\*+$/g, '').trim()}
                          </a>
                        ) : (
                          <div 
                            className="ai-insights-location-title" 
                            style={{ 
                              flexShrink: 0,
                              display: 'inline-block',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              maxWidth: window.innerWidth <= 768 ? '100%' : '40%'
                            }}
                          >
                            {(loc.name || '').replace(/^\*+|\*+$/g, '').trim()}
                          </div>
                        )}
                        
                        {/* Attributes for Locations - Inline */}
                        {loc.attributes && loc.attributes.length > 0 && (
                          <div style={{ 
                            fontSize: '0.85em',
                            color: '#666',
                            flexShrink: 0,
                            display: 'inline-block',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            maxWidth: '30%'
                          }}>
                            {loc.attributes.join(', ')}
                          </div>
                        )}
                        
                        {/* Rating Stars and Count */}
                        {(loc.rating || loc.total_ratings) && (
                          <div style={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: '4px',
                            fontSize: '0.9em',
                            color: '#666',
                            flexShrink: 0,
                            whiteSpace: 'nowrap'
                          }}>
                            {loc.rating && (
                              <>
                                <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                                  {[1, 2, 3, 4, 5].map(star => (
                                    <span
                                      key={star}
                                      style={{
                                        color: star <= Math.round(loc.rating) ? '#ffa500' : '#ddd',
                                        fontSize: '14px',
                                        display: 'inline-block'
                                      }}
                                    >
                                      ★
                                    </span>
                                  ))}
                                </div>
                                <span style={{ fontWeight: '500', marginLeft: '2px', display: 'inline-block' }}>
                                  {loc.rating.toFixed(1)}
                                </span>
                              </>
                            )}
                            {loc.total_ratings && (
                              <span style={{ color: '#888', display: 'inline-block' }}>
                                ({loc.total_ratings.toLocaleString()})
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                   <div className="ai-insights-location-body">{loc.description}</div>
                   <br />
                   {/* Address - Second row */}
                      {loc.address && (
                        <div style={{ 
                          fontSize: '0.85em', 
                          color: '#666', 
                          display: 'block',
                          marginBottom: '4px',
                          width: '100%'
                        }}>
                          📍 {loc.address}
                        </div>
                      )}
                      
                      {/* Opening Hours - Third row */}
                      {loc.openingHours && (
                        <div style={{ 
                          fontSize: '0.85em', 
                          color: '#666', 
                          display: 'block',
                          marginBottom: '4px',
                          width: '100%'
                        }}>
                          🕐 {loc.openingHours}
                        </div>
                      )}
                      
                      {/* Phone - Fourth row */}
                      {loc.phoneNumber && (
                        <div style={{ 
                          fontSize: '0.85em', 
                          color: '#666', 
                          display: 'block',
                          marginBottom: '4px',
                          width: '100%'
                        }}>
                          📞 {loc.phoneNumber}
                        </div>
                      )}
                  <div style={{ flex: 1 }} /> {/* Spacer to push buttons to the bottom */}
                  
                  {/* Dropdown positioned on left side when adding */}
                  {showInlineAdd && (
                    <div style={{
                      position: 'absolute',
                      bottom: '26px',
                      left: '16px',
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                      background: '#f8f9fa',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      padding: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      zIndex: 10
                    }}>
                      <select
                        value={inlineSelectedDayIdx !== null ? inlineSelectedDayIdx : ''}
                        onChange={(e) => setInlineSelectedDayIdx(parseInt(e.target.value))}
                        style={{
                          border: '1px solid #ccc',
                          background: 'white',
                          fontSize: '12px',
                          padding: '6px 8px',
                          borderRadius: '4px',
                          outline: 'none',
                          minWidth: '152px',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Select Day</option>
                        {trip.map((day, dayIdx) => (
                          <option key={dayIdx} value={dayIdx}>
                            {day.description || `Day ${dayIdx + 1}`}
                          </option>
                        ))}
                      </select>
                      <button
                        className="ai-insights-selection-btn add"
                        onClick={async () => {
                          if (inlineSelectedDayIdx !== null) {
                            await handleAddToTrip(loc, inlineSelectedDayIdx);
                            setAddedPlaces(prev => new Set([...prev, idx]));
                            setRemovedPlaces(prev => {
                              const newSet = new Set(prev);
                              newSet.delete(idx);
                              return newSet;
                            });
                            setInlineAddIdx(null);
                            setInlineSelectedDayIdx(null);
                          }
                        }}
                        title="Confirm add"
                        style={{
                          background: '#4CAF50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          marginLeft: '28px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        ✓
                      </button>
                      <button
                        className="ai-insights-selection-btn cancel"
                        onClick={() => {
                          setInlineAddIdx(null);
                          setInlineSelectedDayIdx(null);
                        }}
                        title="Cancel"
                        style={{
                          background: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        ✗
                      </button>
                    </div>
                  )}
                  
                  {/* Show day label when insight is already in trip */}
                  {!showInlineAdd && isAdded && !isRemoved && (
                    <div style={{
                      position: 'absolute',
                      bottom: '26px',
                      left: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      background: '#e8f5e8',
                      border: '1px solid #4CAF50',
                      borderRadius: '6px',
                      padding: '4px 8px',
                      fontSize: '13px',
                      color: '#2e7d32',
                      fontWeight: '500'
                    }}>
                      📍 {getInsightTripDay(loc)}
                    </div>
                  )}
                  
                  {/* Add/Remove buttons positioned on right side */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      marginTop: 8,
                      gap: '8px',
                      position: 'absolute',
                      bottom: '16px',
                      right: '16px'
                    }}
                  >
                    {!showInlineAdd && (!isAdded || isRemoved) && (
                      <button
                        className="ai-insights-add-btn ai-insights-action-btn"
                        onClick={() => setInlineAddIdx(idx)}
                        title="Add to trip"
                      >
                        +
                      </button>
                    )}
                    
                    {isAdded && !isRemoved && (
                      <button
                        className="ai-insights-remove-btn ai-insights-action-btn"
                        onClick={async () => {
                          await handleRemoveFromTrip(loc);
                          setRemovedPlaces(prev => new Set([...prev, idx]));
                          setAddedPlaces(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(idx);
                            return newSet;
                          });
                        }}
                        title="Remove from trip"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Helper function to check if insight is already in trip
  const isInsightInTrip = (insight) => {
    const insightName = (insight.name || '').replace(/^\*+|\*+$/g, '').trim().toLowerCase();
    return trip.some(day => 
      day.events && day.events.some(event => 
        event.location && event.location.name && 
        event.location.name.toLowerCase() === insightName
      )
    );
  };

  // Helper function to get the day description where insight is located
  const getInsightTripDay = (insight) => {
    const insightName = (insight.name || '').replace(/^\*+|\*+$/g, '').trim().toLowerCase();
    const dayIndex = trip.findIndex(day => 
      day.events && day.events.some(event => 
        event.location && event.location.name && 
        event.location.name.toLowerCase() === insightName
      )
    );
    if (dayIndex !== -1) {
      return trip[dayIndex].description || `Day ${dayIndex + 1}`;
    }
    return null;
  };

  // Helper function to add insight to trip
  const handleAddToTrip = async (insight, dayIndex) => {
    if (!tripId || dayIndex < 0 || dayIndex >= trip.length) return;

    try {
      // Create a new event object
      const maxOrderInDay = trip[dayIndex].events && Array.isArray(trip[dayIndex].events)
        ? Math.max(...trip[dayIndex].events.map(ev => ev.orderInDay || 0))
        : 0;
      
      const event = {
        eventType: insightType === 'Food' ? "Restaurant" : "Location",
        description: (insight.name || '').replace(/^\*+|\*+$/g, '').trim(),
        longDescription: insight.description || '',
        location: {
          name: (insight.name || '').replace(/^\*+|\*+$/g, '').trim(),
          lat: insight.coordinate?.latitude || 0,
          lng: insight.coordinate?.longitude || 0,
          url: insight.infoUrl || insight.webUrl || '',
          zoom: 15,
          photoUrl: insight.photoUrl || '',
          rating: insight.rating || 0,
          formatted_address: insight.address || '',
          formatted_phone_number: insight.businessPhone || '',
          place_id: insight.place_id || '',
          ...(insightType === 'Food' && {
            priceLevel: insight.priceLevel || 0,
            cuisineType: insight.cuisineType || '',
            openingHours: insight.openingHours || ''
          })
        },
        orderInDay: maxOrderInDay + 1
      };

      // Update the trip
      const updatedTrip = [...trip];
      updatedTrip[dayIndex] = {
        ...updatedTrip[dayIndex],
        events: [...(updatedTrip[dayIndex].events || []), event]
      };

      // Send update to backend
      const response = await fetch(`${BACKEND_URL}/newtrips/${tripId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          days: updatedTrip
        })
      });

      if (response.ok) {
        setTrip(updatedTrip);
        console.log('Successfully added to trip');
      } else {
        throw new Error('Failed to update trip');
      }
    } catch (error) {
      console.error('Error adding to trip:', error);
    }
  };

  // Helper function to remove insight from trip
  const handleRemoveFromTrip = async (insight) => {
    if (!tripId) return;

    try {
      const insightName = (insight.name || '').replace(/^\*+|\*+$/g, '').trim().toLowerCase();
      
      // Find the day and event to remove
      const updatedTrip = trip.map(day => ({
        ...day,
        events: (day.events || []).filter(event => 
          !(event.location && event.location.name && 
            event.location.name.toLowerCase() === insightName)
        )
      }));

      // Send update to backend
      const response = await fetch(`${BACKEND_URL}/newtrips/${tripId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          days: updatedTrip
        })
      });

      if (response.ok) {
        setTrip(updatedTrip);
        console.log('Successfully removed from trip');
      } else {
        throw new Error('Failed to update trip');
      }
    } catch (error) {
      console.error('Error removing from trip:', error);
    }
  };

  const enabledCityNames = (location?.state?.cities || []).filter((_, idx) => enabledCities[idx]);

  return (
    <>
      <Navbar />
      <div className="trip-ai-page-stack">
        {renderConfigPane()}
        <div className="trip-ai-root">
          {loading && (
            <div className="trip-ai-loading-center">
              <span className="trip-ai-loading-text">Loading AI insights</span>
            </div>
          )}
          {error && <div style={{ color: 'red' }}>{error}</div>}
          {!loading && !error && !hasSearched && (
            <div className="trip-ai-loading-center" style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: '1.1em', color: '#666', marginBottom: '10px' }}>
                To initiate a search for {insightType.toLowerCase()}, press the Search icon
              </div>
            </div>
          )}
          {!loading && !error && hasSearched && (window.innerWidth <= 768 ? renderInsightsMobile(insights) : renderInsights(insights))}
        </div>
      </div>

      {/* Daily Quota Limit Popup */}
      {showQuotaPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            margin: '20px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '16px'
            }}>
              ⚠️
            </div>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '1.4em',
              color: '#333',
              fontWeight: '600'
            }}>
              Daily Quota Reached
            </h3>
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '1em',
              color: '#666',
              lineHeight: '1.5'
            }}>
              You have reached your daily quota limit. You can try again tomorrow or purchase a premium account with higher daily quota.
            </p>
            <button
              onClick={() => setShowQuotaPopup(false)}
              style={{
                backgroundColor: '#2a5d9f',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '1em',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#1e4a7a'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#2a5d9f'}
            >
              Understood
            </button>
          </div>
        </div>
      )}
    </>
  );
}
